import { fetchQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { Currency } from '@sequencehq/api/utils'
import { delay } from '@sequencehq/utils'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { indexSyncByMonth, syncForMonth } from '../xeroSync'

const POLL_INTERVAL_MS = 2500
// ~2 minutes of polling before we stop waiting on a slow/stuck push.
const MAX_ATTEMPTS = 48

export type XeroPushOutcome = { failed: number; timedOut: boolean }

/**
 * Poll the Xero sync status until the given months finish pushing (nothing left
 * pending or posting), then report how many journals failed. Drives the push
 * progress toast so the modal can close immediately.
 *
 * The push is async (the endpoint returns 202 and queues the work), so we wait a
 * beat before the first read to let the backend register the journals as pending,
 * and give up after MAX_ATTEMPTS so a stuck push never hangs the toast forever.
 */
export const waitForXeroPushToSettle = async (
  monthKeys: string[],
  currency: Currency
): Promise<XeroPushOutcome> => {
  const poll = async (attempt: number): Promise<XeroPushOutcome> => {
    await delay(POLL_INTERVAL_MS)

    const data = await fetchQuery(
      apiQueryClient,
      dashboardv99990101Client.getJournalPushXeroSyncStatus,
      { currency },
      { staleTime: 0 }
    )
    const byMonth = indexSyncByMonth(data?.months ?? [])
    const syncs = monthKeys.map(monthKey => syncForMonth(byMonth, monthKey))

    const stillPushing = syncs.some(
      sync => sync.posting > 0 || sync.pending > 0
    )
    const failed = syncs.reduce((total, sync) => total + sync.failed, 0)

    if (!stillPushing) {
      return { failed, timedOut: false }
    }
    if (attempt + 1 >= MAX_ATTEMPTS) {
      return { failed, timedOut: true }
    }
    return poll(attempt + 1)
  }

  return poll(0)
}
